import apiClient from '../api/client'
import { SUPPORT_PHONE_DISPLAY } from '../config'
import { store } from '../state/store'
import type { BatteryOption, Chip, Driver, RowKey } from '../state/types'
import { fmtFt, formatPhone, formatSlotLabel, normalizePhone } from '../utils/format'
import { notifyCheckoutStarted, notifyOrderCart, notifyVehicleSelected } from '../utils/orderFunnel'
import { dateToHu, priceToHu, slotTimeToHu, slotToHu } from '../utils/sayHu'
import { logDiag, voiceEvent } from '../utils/voiceEvents'

/**
 * The OpenAI Realtime bridge (plan §3.2): browser ↔ OpenAI over WebRTC with a
 * server-minted ephemeral secret; Realtime events drive the handoff's visual
 * state machine, tools drive the cards — the UI renders from STRUCTURED DATA
 * only (catalogue rows by id, validated user input), never from model prose.
 * /orderInstall is submitted by the UI on „Megrendelem", never by the model.
 */

const OPENAI_CALLS_URL = 'https://api.openai.com/v1/realtime/calls'
const TIME_WARN_S = 480
const TIME_MAX_S = 600
const CARD_MS = 2700
const CARD_OUT_MS = 300

interface VehicleRow {
	id: number
	brand: string
	model: string
	model_type: string
	fuel_type: string
	display_text: string
}

interface BatteryRow {
	id: number
	name: string
	brand: string
	capacity: number
	cca: number
	warranty: number
	price: number
	price_total: number
	image: string
	description: string
}

/** Similarity of the misheard words to a vehicle row's type/model tokens:
    exact token = 3, prefix (≥3 chars) = 2, Levenshtein ≤ 2 = 1. */
function fuzzyScore(misheard: string, row: VehicleRow): number {
	const targets = `${row.model_type ?? ''} ${row.model ?? ''}`.toLowerCase().split(/\s+/).filter((t) => t.length >= 2)
	let best = 0
	for (const heard of misheard.split(/\s+/)) {
		if (heard.length < 2) continue
		for (const t of targets) {
			if (t === heard) best = Math.max(best, 3)
			else if (heard.length >= 3 && (t.startsWith(heard) || heard.startsWith(t))) best = Math.max(best, 2)
			else if (levenshtein(heard, t) <= 2 && Math.min(heard.length, t.length) >= 4) best = Math.max(best, 1)
		}
	}
	return best
}

function levenshtein(a: string, b: string): number {
	if (Math.abs(a.length - b.length) > 2) return 3
	const dp = Array.from({ length: a.length + 1 }, (_, i) => i)
	for (let j = 1; j <= b.length; j++) {
		let prev = dp[0]
		dp[0] = j
		for (let i = 1; i <= a.length; i++) {
			const tmp = dp[i]
			dp[i] = Math.min(dp[i] + 1, dp[i - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
			prev = tmp
		}
	}
	return dp[a.length]
}

interface OrderDraft {
	vehicleId?: number
	vehicleName?: string
	vehicleRow?: VehicleRow
	productId?: number
	battery?: BatteryRow
	address?: { pc: string; city: string; street: string; hn: string; inZone: boolean }
	slot?: { date: string; time: string; label: string }
	contact?: { name: string; phone: string; email: string }
	payment?: 'cash' | 'card'
}

export class RealtimeDriver implements Driver {
	private alive = true
	private pc: RTCPeerConnection | null = null
	private dc: RTCDataChannel | null = null
	private micStream: MediaStream | null = null
	private micTrack: MediaStreamTrack | null = null
	private audioEl: HTMLAudioElement | null = null
	/** No usable microphone for this session — denied, unavailable, or declined
	    up front via „Inkább írok". All three run the same typed-only flow. */
	private micDenied = false

	private holdEnabled = false
	private draftEnabled = false
	private draftMinted = false
	private draftShowing = false

	private order: OrderDraft = {}
	private vehicleCandidates = new Map<number, VehicleRow>()
	private batteryCatalogue = new Map<number, BatteryRow>()

	private hadAudioInResponse = false
	private responseActive = false
	private finalConfirmShown = false
	private submitting = false

	private timers = new Set<number>()
	private startedAt = 0
	private warned = false

	/* ---------------- lifecycle ---------------- */

	async start(withMic: boolean): Promise<void> {
		let mint: Record<string, unknown>
		try {
			const res = await apiClient.post('/voice/session', {})
			mint = res.data as Record<string, unknown>
		} catch (err: unknown) {
			const data = (err as { response?: { data?: { message?: string } } }).response?.data
			logDiag('mint_fail', typeof data?.message === 'string' ? data.message : 'network')
			this.degrade(typeof data?.message === 'string' ? data.message : undefined)
			return
		}

		if (!mint || mint.status !== 'ok' || typeof mint.client_secret !== 'string') {
			logDiag('mint_fail', String(mint?.status ?? 'no_response'))
			this.degrade()
			return
		}

		this.holdEnabled = !!mint.hold_enabled
		this.draftEnabled = !!mint.draft_enabled
		const model = typeof mint.model === 'string' ? mint.model : 'gpt-realtime'

		// Mic AFTER the entrance gesture; denial degrades to the typed-only
		// session (the orb still glows on text — handoff §2.1 delta 6).
		// getUserMedia only exists in a SECURE CONTEXT (https or localhost):
		// on http://akkumost.test navigator.mediaDevices is undefined, so local
		// 2-way audio needs the localhost door (http://localhost:8018) or the
		// vite dev server — prod https://akkumost.hu is always fine.
		if (!withMic) {
			// „Inkább írok" — a choice, not a failure, so the permission prompt
			// never appears and this must NOT report voice_micdenied (that metric
			// is the denial rate). Downstream it is the same typed-only session.
			this.micDenied = true
			logDiag('typed_start', 'user choice')
		} else if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
			this.micDenied = true
			console.warn(
				'[akkumost] Mikrofon nem elérhető: nem biztonságos kontextus (http). ' +
					'Helyi teszthez: http://localhost:8018 vagy `npm run dev` (localhost:5173). Élesben (https) működik.',
			)
			voiceEvent('voice_micdenied')
		} else {
			try {
				this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
				this.micTrack = this.micStream.getAudioTracks()[0] ?? null
			} catch {
				this.micDenied = true
				voiceEvent('voice_micdenied')
			}
		}

		try {
			await this.connect(mint.client_secret)
			logDiag('connected', `${model}${this.micDenied ? ' (typed-only)' : ''}`)
		} catch (err) {
			console.error('Realtime connect failed', err)
			logDiag('connect_fail', err instanceof Error ? err.message : String(err))
			this.degrade()
			return
		}

		this.startedAt = Date.now()
		this.st(() => this.tickClock(), 30000)
		store.setOrb('idle')
		if (this.micDenied) store.set({ statusHint: 'dictate' })
	}

	private async connect(secret: string): Promise<void> {
		const pc = new RTCPeerConnection()
		this.pc = pc

		this.audioEl = document.createElement('audio')
		this.audioEl.autoplay = true
		pc.ontrack = (e) => {
			if (this.audioEl) this.audioEl.srcObject = e.streams[0]
		}

		if (this.micTrack && this.micStream) {
			pc.addTrack(this.micTrack, this.micStream)
		} else {
			pc.addTransceiver('audio', { direction: 'recvonly' })
		}

		const dc = pc.createDataChannel('oai-events')
		this.dc = dc
		dc.onmessage = (e) => {
			try {
				this.handleEvent(JSON.parse(e.data))
			} catch (err) {
				console.error('bad realtime event', err)
			}
		}
		dc.onopen = () => {
			// Elicit the greeting — with server VAD the model would otherwise
			// wait silently for the user to speak first. In typed-only mode the
			// assistant must know: otherwise it keeps saying „Mondja..." to a
			// customer who can only write.
			if (this.micDenied) {
				this.createItem(
					'user',
					'[RENDSZER] A vevő mikrofonja nem elérhető, ezért kizárólag ÍRÁSBAN fog válaszolni. Köszöntse, és kérje, hogy írja be a válaszait — a „mondja" helyett használja az „írja be" fordulatot.',
				)
			} else {
				this.send({ type: 'response.create' })
			}
		}

		const offer = await pc.createOffer()
		await pc.setLocalDescription(offer)

		// No model query param: the model is part of the minted session config
		// (verified against the current WebRTC guide, 2026-08).
		const resp = await fetch(OPENAI_CALLS_URL, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${secret}`,
				'Content-Type': 'application/sdp',
			},
			body: offer.sdp,
		})
		if (!resp.ok) throw new Error(`SDP exchange failed: ${resp.status}`)
		const answer = await resp.text()
		await pc.setRemoteDescription({ type: 'answer', sdp: answer })
	}

	dispose(): void {
		this.alive = false
		this.timers.forEach((t) => window.clearTimeout(t))
		this.disconnect()
		store.stopHold()
	}

	private disconnect(): void {
		try {
			this.dc?.close()
			this.pc?.close()
			this.micStream?.getTracks().forEach((t) => t.stop())
		} catch {
			/* teardown is best-effort */
		}
		this.dc = null
		this.pc = null
	}

	private degrade(message?: string): void {
		store.setLine(message || 'A hangasszisztens jelenleg nem elérhető — kérjük, hívjon minket a jobb felső Hívás gombbal.')
		store.set({ orb: 'muted', statusHint: 'none' })
	}

	private st(fn: () => void, ms: number): void {
		const id = window.setTimeout(() => {
			this.timers.delete(id)
			if (this.alive) fn()
		}, ms)
		this.timers.add(id)
	}

	private tickClock(): void {
		if (!this.dc || store.get().done) return
		const elapsed = (Date.now() - this.startedAt) / 1000
		if (elapsed >= TIME_MAX_S) {
			this.disconnect()
			store.setLine('Elértük a beszélgetés időkeretét. Ha maradt kérdése, hívjon minket a Hívás gombbal — vagy töltse újra az oldalt.')
			store.set({ orb: 'muted', listening: false, statusHint: 'none' })
			return
		}
		if (elapsed >= TIME_WARN_S && !this.warned) {
			this.warned = true
			this.createItem('system', '[RENDSZER] A beszélgetésből körülbelül 2 perc van hátra. Kezdje lezárni: foglalja össze, mi van meg, és mi hiányzik még.')
		}
		this.st(() => this.tickClock(), 30000)
	}

	/* ---------------- Realtime events → the visual state machine ---------------- */

	private handleEvent(ev: { type: string } & Record<string, unknown>): void {
		switch (ev.type) {
			case 'input_audio_buffer.speech_started':
				store.set({ listening: true, orb: 'listening', statusHint: 'none' })
				break

			case 'input_audio_buffer.speech_stopped':
				store.set({ listening: false })
				if (store.get().orb === 'listening') store.setOrb('thinking')
				break

			case 'response.created':
				this.responseActive = true
				this.hadAudioInResponse = false
				if (store.get().orb !== 'speaking') store.setOrb('thinking')
				break

			case 'output_audio_buffer.started':
				this.hadAudioInResponse = true
				store.setOrb('speaking')
				break

			case 'output_audio_buffer.stopped':
			case 'output_audio_buffer.cleared':
				if (store.get().orb === 'speaking') store.setOrb('idle')
				this.maybeDictateHint()
				break

			case 'response.output_audio_transcript.delta':
			case 'response.audio_transcript.delta': {
				const delta = typeof ev.delta === 'string' ? ev.delta : ''
				this.hadAudioInResponse = true
				// The transcript delta is the RELIABLE speaking signal — the
				// output_audio_buffer.* server events are undocumented (2026-08
				// verification), so the orb must not depend on them.
				store.setOrb('speaking')
				if (store.get().userEcho) store.set({ userEcho: '' })
				if (this.lineFresh) {
					store.appendLine(delta)
				} else {
					this.lineFresh = true
					store.setLine(delta)
				}
				break
			}

			case 'response.output_audio_transcript.done':
			case 'response.audio_transcript.done': {
				const transcript = typeof ev.transcript === 'string' ? ev.transcript : ''
				if (transcript) {
					if (this.lineFresh) store.set({ assistantLine: transcript })
					else store.setLine(transcript)
				}
				this.lineFresh = false
				break
			}

			case 'conversation.item.input_audio_transcription.completed': {
				const transcript = typeof ev.transcript === 'string' ? ev.transcript : ''
				if (transcript.trim()) store.setEcho(transcript.trim())
				break
			}

			case 'response.function_call_arguments.done':
				// Primary tool-dispatch path (payload verified 2026-08:
				// call_id + name + arguments-as-JSON-string).
				this.dispatchToolCall(
					typeof ev.name === 'string' ? ev.name : '',
					typeof ev.call_id === 'string' ? ev.call_id : '',
					typeof ev.arguments === 'string' ? ev.arguments : '{}',
				)
				break

			case 'response.done': {
				this.responseActive = false
				// Belt-and-braces: the official function-calling walkthrough also
				// reads calls off response.output[] — dedup by call_id makes the
				// two paths safe together.
				const output = (ev.response as { output?: Array<Record<string, unknown>> } | undefined)?.output
				if (Array.isArray(output)) {
					for (const item of output) {
						if (item.type !== 'function_call') continue
						this.dispatchToolCall(
							typeof item.name === 'string' ? item.name : '',
							typeof item.call_id === 'string' ? item.call_id : '',
							typeof item.arguments === 'string' ? item.arguments : '{}',
						)
					}
				}
				if (store.get().orb === 'thinking') store.setOrb('idle')
				this.maybeDictateHint()
				break
			}

			case 'response.cancelled':
				this.responseActive = false
				if (store.get().orb === 'speaking' || store.get().orb === 'thinking') store.setOrb('idle')
				break

			case 'error': {
				console.error('Realtime error event', ev)
				const e = ev.error as { message?: string; code?: string } | undefined
				logDiag('rt_error', `${e?.code ?? '?'}: ${e?.message ?? JSON.stringify(ev).slice(0, 120)}`)
				break
			}

			default:
				break
		}
	}

	private lineFresh = false
	private dispatchedCalls = new Set<string>()

	/** Single entry for both dispatch paths (arguments.done + response.done). */
	private dispatchToolCall(name: string, callId: string, rawArgs: string): void {
		if (!name || !callId || this.dispatchedCalls.has(callId)) return
		this.dispatchedCalls.add(callId)
		let args: Record<string, unknown> = {}
		try {
			args = JSON.parse(rawArgs)
		} catch {
			/* leave empty */
		}
		void this.executeTool(name, args, callId)
	}

	/** „Mondja el — vagy írja be lent" only when dictation is expected: no
	    chips, no content card, conversation still open. */
	private maybeDictateHint(): void {
		const s = store.get()
		if (s.done || this.responseActive) return
		const busy = !!(s.card || s.options || s.price || s.success || s.draft)
		store.set({ statusHint: s.chips.length === 0 && !busy ? 'dictate' : 'none' })
	}

	/* ---------------- outbound events ---------------- */

	private send(payload: Record<string, unknown>): void {
		if (this.dc && this.dc.readyState === 'open') {
			this.dc.send(JSON.stringify(payload))
		}
	}

	/** Always user-role: mid-session system-role injection is not documented
	    (2026-08 verification) — system-ish notes carry the [RENDSZER] prefix
	    instead, which the instructions tell the model to obey silently. */
	private createItem(_role: 'user' | 'system', text: string): void {
		this.send({
			type: 'conversation.item.create',
			item: {
				type: 'message',
				role: 'user',
				content: [{ type: 'input_text', text }],
			},
		})
		this.send({ type: 'response.create' })
	}

	private cancelActiveResponse(): void {
		if (this.responseActive) this.send({ type: 'response.cancel' })
		this.send({ type: 'output_audio_buffer.clear' })
		if (store.get().orb === 'speaking') store.setOrb('idle')
	}

	private sendUserText(text: string): void {
		store.setEcho(text)
		store.set({ chips: [], statusHint: 'none' })
		this.cancelActiveResponse()
		this.createItem('user', text)
		store.setOrb('thinking')
	}

	/* ---------------- Driver interface (UI intents) ---------------- */

	pickChip(chip: Chip): void {
		if (chip.id === 'order') {
			void this.submitOrder()
			return
		}
		if (chip.id === 'modify') {
			store.set({ price: null })
			this.finalConfirmShown = false
			this.sendUserText('Még módosítanék.')
			return
		}
		this.sendUserText(chip.label)
	}

	pickOption(opt: BatteryOption): void {
		const id = parseInt(opt.id, 10)
		if (!this.batteryCatalogue.has(id) || this.order.productId === id) return
		const shown = (store.get().options || []).map((o) =>
			o.id === opt.id ? { ...o, state: 'chosen' as const } : { ...o, state: 'dimmed' as const },
		)
		store.setEcho(`A(z) ${opt.name} legyen.`)
		store.set({ options: shown })
		this.st(() => {
			store.set({ options: null })
			void this.fileBattery(id)
		}, 1300)
		this.cancelActiveResponse()
		this.createItem('user', `A(z) ${opt.name} akkumulátort választom.`)
	}

	submitText(text: string): void {
		this.sendUserText(text)
	}

	toggleMic(): void {
		if (!this.micTrack) return
		const muted = !this.micTrack.enabled
		if (muted) {
			this.micTrack.enabled = true
			store.set({ orb: 'idle' })
		} else {
			this.micTrack.enabled = false
			store.set({ orb: 'muted', listening: false })
		}
	}

	editRow(_key: RowKey, label: string): void {
		if (store.get().done) return
		store.set({ expanded: false })
		this.sendUserText(`A(z) „${label.toLowerCase()}" adatát módosítanám.`)
	}

	callTapped(): void {
		if (!this.draftEnabled || this.draftShowing || store.get().done) return
		this.draftShowing = true
		apiClient
			.post('/voice/draft', { payload: this.draftPayload(), mint_code: true })
			.then((res) => {
				const code = (res.data as { code?: string }).code
				if (!code || !this.alive) {
					this.draftShowing = false
					return
				}
				this.draftMinted = true
				store.set({ draft: { code, phone: SUPPORT_PHONE_DISPLAY } })
				this.st(() => {
					store.set({ draft: null })
					this.draftShowing = false
				}, 9000)
			})
			.catch(() => {
				this.draftShowing = false
			})
	}

	/* ---------------- tools ---------------- */

	private async executeTool(name: string, args: Record<string, unknown>, callId: string): Promise<void> {
		let result: unknown
		try {
			result = await this.runTool(name, args)
		} catch (err) {
			console.error(`tool ${name} failed`, err)
			logDiag('tool_exception', `${name}: ${err instanceof Error ? err.message : String(err)}`)
			result = { ok: false, error: 'internal_error', hint: 'Átmeneti hiba — próbálja meg EGYSZER újra; ha ismét hiba, ajánlja a Hívás gombot.' }
		}
		const failed = typeof result === 'object' && result !== null && (result as { ok?: boolean }).ok === false
		if (failed) {
			logDiag('tool_fail', `${name}: ${(result as { error?: string }).error ?? '?'}`)
		}
		this.send({
			type: 'conversation.item.create',
			item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(result) },
		})
		// UI-only tools need no follow-up response when the model already spoke
		// in the same turn; data/commitment results the model must react to.
		if (name === 'suggest_replies' && this.hadAudioInResponse) return
		this.send({ type: 'response.create' })
	}

	private async runTool(name: string, args: Record<string, unknown>): Promise<unknown> {
		switch (name) {
			case 'search_vehicle':
				return this.toolSearchVehicle(String(args.search ?? ''), args.fuel_type ? String(args.fuel_type) : undefined)
			case 'choose_vehicle':
				return this.toolChooseVehicle(Number(args.vehicle_id ?? 0))
			case 'get_batteries':
				return this.toolGetBatteries(Number(args.vehicle_id ?? this.order.vehicleId ?? 0))
			case 'choose_battery':
				return this.fileBattery(Number(args.product_id ?? 0))
			case 'set_address':
				return this.toolSetAddress(args)
			case 'get_days':
				return this.toolGetDays()
			case 'get_hours':
				return this.toolGetHours(String(args.date ?? ''))
			case 'choose_slot':
				return this.toolChooseSlot(String(args.date ?? ''), String(args.time ?? ''))
			case 'set_contact':
				return this.toolSetContact(String(args.name ?? ''), String(args.phone ?? ''), args.email ? String(args.email) : '')
			case 'set_payment':
				return this.toolSetPayment(String(args.method ?? ''))
			case 'suggest_replies':
				return this.toolSuggestReplies(args)
			default:
				return { ok: false, error: `unknown_tool:${name}` }
		}
	}

	private async toolSearchVehicle(search: string, fuelType?: string): Promise<unknown> {
		if (search.trim().length < 2) return { ok: false, error: 'query_too_short' }
		const rows = await this.vehicleSearchApi(search.trim(), fuelType)

		if (rows.length > 0) {
			return { ok: true, count: rows.length, candidates: this.registerCandidates(rows) }
		}

		// Fuzzy fallback (misheard model names — "Skoda Fabio"): search the
		// FIRST term (the brand) alone, then rank that brand's real types by
		// similarity to the unmatched words → "A Skoda Fabiára gondolt?".
		const terms = search.trim().split(/\s+/)
		if (terms.length >= 2) {
			const brandRows = await this.vehicleSearchApi(terms[0], fuelType, 500)
			if (brandRows.length > 0) {
				const misheard = terms.slice(1).join(' ').toLowerCase()
				const scored = brandRows
					.map((r) => ({ r, score: fuzzyScore(misheard, r) }))
					.filter((s) => s.score > 0)
					.sort((a, b) => b.score - a.score)
				if (scored.length > 0) {
					// One suggestion set per distinct type name, best variants first.
					const seen = new Set<string>()
					const suggestions: VehicleRow[] = []
					for (const s of scored) {
						const key = `${s.r.brand}|${s.r.model_type}`
						if (seen.has(key) && suggestions.length >= 4) continue
						seen.add(key)
						suggestions.push(s.r)
						if (suggestions.length >= 8) break
					}
					logDiag('search_fuzzy', `"${search}" -> ${suggestions[0]?.display_text ?? '?'}`)
					return {
						ok: true,
						count: suggestions.length,
						exact_match: false,
						note: `Nincs pontos találat erre: „${search}" — az alábbiak hasonló nevű, létező típusok ennél a márkánál. Ajánlja fel a legvalószínűbbet („A(z) X-ra gondolt?").`,
						candidates: this.registerCandidates(suggestions),
					}
				}
			}
		}

		logDiag('search_empty', search)
		return { ok: true, count: 0, candidates: [], note: 'Nincs találat — kérdezze meg lépésenként: márka, típus, évjárat, motor.' }
	}

	private async vehicleSearchApi(search: string, fuelType?: string, cap = 60): Promise<VehicleRow[]> {
		const params: Record<string, string> = { search }
		if (fuelType) params.fuel_type = fuelType
		const res = await apiClient.get('/vehicle/search', { params })
		const data = res.data?.data as { success?: boolean; results?: VehicleRow[] } | undefined
		return (data?.results ?? []).slice(0, cap)
	}

	private registerCandidates(rows: VehicleRow[]): { vehicle_id: number; label: string }[] {
		this.vehicleCandidates.clear()
		const trimmed = rows.slice(0, 12)
		for (const row of trimmed) this.vehicleCandidates.set(Number(row.id), row)
		return trimmed.map((r) => ({ vehicle_id: Number(r.id), label: r.display_text }))
	}

	private async toolChooseVehicle(vehicleId: number): Promise<unknown> {
		const row = this.vehicleCandidates.get(vehicleId)
		if (!row) return { ok: false, error: 'unknown_vehicle_id' }
		this.order.vehicleId = vehicleId
		this.order.vehicleRow = row
		this.order.vehicleName = row.display_text
		notifyVehicleSelected(vehicleId)
		this.fileFact('car', 'Az Ön autója', row.display_text, { note: '63 575 jármű adatbázisából azonosítva' })
		void this.upsertDraft()
		return { ok: true, vehicle: row.display_text }
	}

	private async toolGetBatteries(vehicleId: number): Promise<unknown> {
		if (!vehicleId) return { ok: false, error: 'no_vehicle' }
		const res = await apiClient.get('/battery/list', { params: { vehicle_id: vehicleId } })
		const raw = res.data?.data as Record<string, BatteryRow> | BatteryRow[] | undefined
		const rows: BatteryRow[] = (Array.isArray(raw) ? raw : Object.values(raw ?? {}))
			.map((r) => ({ ...r, id: Number(r.id), price: Number(r.price), price_total: Number(r.price_total) }))
			.filter((r) => r.id > 0 && r.price_total > 0)
			.sort((a, b) => a.price_total - b.price_total)
		if (rows.length === 0) return { ok: false, error: 'no_batteries' }

		// 2-3 options: cheapest, middle (AJÁNLOTT), premium — deterministic
		// structure, the model's spoken recommendation carries the reasoning.
		let shown: BatteryRow[]
		if (rows.length <= 3) shown = rows
		else shown = [rows[0], rows[Math.floor(rows.length / 2)], rows[rows.length - 1]]
		const recommendedIdx = shown.length >= 2 ? Math.floor((shown.length - 1) / 2) : 0

		this.batteryCatalogue.clear()
		for (const row of shown) this.batteryCatalogue.set(row.id, row)

		store.set({
			options: shown.map((row, i) => ({
				id: String(row.id),
				name: row.name,
				short: row.name,
				spec: this.batterySpec(row),
				price: fmtFt(row.price_total),
				tag: i === recommendedIdx ? 'Ajánlott' : undefined,
				reason: row.description || '',
				img: row.image,
				state: 'normal' as const,
			})),
			chips: [],
		})

		return {
			ok: true,
			options: shown.map((row, i) => ({
				product_id: row.id,
				name: row.name,
				spec: this.batterySpec(row),
				price_total: row.price_total,
				price_total_text: fmtFt(row.price_total),
				price_say: priceToHu(row.price_total),
				recommended: i === recommendedIdx,
			})),
		}
	}

	private batterySpec(row: BatteryRow): string {
		const parts: string[] = []
		if (row.capacity) parts.push(`${row.capacity}Ah`)
		if (row.cca) parts.push(`${row.cca}A`)
		if (row.warranty) parts.push(`${row.warranty} év garancia`)
		return parts.join(' · ')
	}

	private async fileBattery(productId: number): Promise<unknown> {
		const row = this.batteryCatalogue.get(productId)
		if (!row) return { ok: false, error: 'unknown_product_id' }
		if (this.order.productId === productId) return { ok: true, already: true }
		this.order.productId = productId
		this.order.battery = row
		notifyOrderCart(productId)
		store.set({ options: null, total: fmtFt(row.price_total) })
		this.fileFact('bat', 'Választott akkumulátor', `${row.name} — ${fmtFt(row.price_total)}`, {
			green: true,
			note: `Garantáltan megfelel az Ön autójához${this.order.vehicleName ? ` · ${this.order.vehicleName}` : ''}`,
			noteGreen: true,
			holdMs: 3200,
		})
		void this.upsertDraft()
		this.maybeFinalConfirm()
		return {
			ok: true,
			battery: row.name,
			price_total: row.price_total,
			price_total_text: fmtFt(row.price_total),
			price_total_say: priceToHu(row.price_total),
		}
	}

	private async toolSetAddress(args: Record<string, unknown>): Promise<unknown> {
		const pc = String(args.postal_code ?? '').replace(/\D+/g, '')
		const street = String(args.street ?? '').trim()
		const hn = String(args.house_number ?? '').trim()
		const city = String(args.city ?? '').trim() || (pc.startsWith('1') ? 'Budapest' : '')
		if (!/^\d{4}$/.test(pc) || street === '' || hn === '') {
			return { ok: false, error: 'invalid_address', hint: 'irányítószám (4 számjegy), utca és házszám kell' }
		}

		// Zone signal: the address DB knows the streets of the delivery area.
		let inZone = false
		try {
			const res = await apiClient.get(`/address/street/${pc}`)
			const list = res.data?.data
			inZone = Array.isArray(list) && list.length > 0
		} catch {
			inZone = false
		}

		this.order.address = { pc, city, street, hn, inZone }
		const display = `${pc} ${city ? `${city}, ` : ''}${street} ${hn}.`
		this.fileFact('addr', 'Helyszín', display, {
			note: inZone ? 'Kiszállási zónán belül — a szerelő Önhöz megy' : undefined,
		})
		void this.upsertDraft()
		this.maybeFinalConfirm()
		return { ok: true, in_zone: inZone, address: display }
	}

	private async toolGetDays(): Promise<unknown> {
		const res = await apiClient.get('/days')
		const days = (res.data?.data ?? []) as { label: string; value: string }[]
		if (days.length === 0) logDiag('days_empty')
		return {
			ok: true,
			days: days.map((d) => ({ label: d.label, value: d.value, say: dateToHu(d.value) })),
		}
	}

	private async toolGetHours(date: string): Promise<unknown> {
		if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
			return { ok: false, error: 'invalid_date', hint: 'A dátumot YYYY-MM-DD formában adja át (a get_days value mezője).' }
		}
		const res = await apiClient.get('/hours', { params: { date } })
		const hours = (res.data?.data ?? []) as { label: string; value: string }[]
		if (hours.length === 0) logDiag('hours_empty', date)
		return {
			ok: true,
			date,
			hours: hours.map((h) => ({ label: h.label, time: h.value.slice(0, 5), say: slotTimeToHu(h.value) })),
		}
	}

	private async toolChooseSlot(date: string, time: string): Promise<unknown> {
		if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}/.test(time)) {
			return { ok: false, error: 'invalid_slot' }
		}
		const normTime = `${time.slice(0, 2)}:00`

		if (this.holdEnabled) {
			try {
				await apiClient.post('/voice/hold', { order_day: date, order_time: normTime })
				store.startHold()
			} catch (err: unknown) {
				const status = (err as { response?: { status?: number } }).response?.status
				if (status === 409) {
					logDiag('hold_conflict', `${date} ${normTime}`)
					return { ok: false, error: 'conflict', hint: 'Ez az időpont már foglalt — kérjen elnézést és ajánljon másik szabad sávot a get_hours listából.' }
				}
				// Hold is a nice-to-have: a hold failure must not block ordering.
				logDiag('hold_fail', `${date} ${normTime} status=${status ?? 'network'}`)
			}
		}

		const label = formatSlotLabel(date, normTime)
		this.order.slot = { date, time: normTime, label }
		this.fileFact('slot', 'Időpont', label, {
			note: this.holdEnabled && store.get().holdOn ? 'Ezt az időpontot lefoglaltuk Önnek.' : 'Ez az időpont jelenleg szabad.',
			noteGreen: true,
			holdMs: 3000,
		})
		void this.upsertDraft()
		this.maybeFinalConfirm()
		return {
			ok: true,
			slot: label,
			say: slotToHu(date, normTime),
			held: this.holdEnabled && store.get().holdOn,
		}
	}

	private async toolSetContact(name: string, phone: string, email: string): Promise<unknown> {
		const normalized = normalizePhone(phone)
		if (name.trim().length < 3) return { ok: false, error: 'invalid_name' }
		if (normalized === '') return { ok: false, error: 'invalid_phone', hint: 'kérje újra a telefonszámot számjegyenként' }
		// orderInstall requires an email for the confirmation mail — without it
		// the submit would fail at the very end of the conversation.
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
			return { ok: false, error: 'invalid_email', hint: 'kérje el az e-mail címet a visszaigazoláshoz' }
		}
		this.order.contact = { name: name.trim(), phone: normalized, email: email.trim() }
		this.fileFact('contact', 'Kapcsolat', `${name.trim()} · ${formatPhone(normalized)}`)
		void this.upsertDraft()
		this.maybeFinalConfirm()
		return { ok: true }
	}

	private async toolSetPayment(method: string): Promise<unknown> {
		if (method !== 'cash' && method !== 'card') return { ok: false, error: 'invalid_method' }
		this.order.payment = method
		this.fileFact('pay', 'Fizetés', `${method === 'card' ? 'Kártyával' : 'Készpénzben'}, a helyszínen`)
		void this.upsertDraft()
		this.maybeFinalConfirm()
		const total = this.order.battery?.price_total
		return {
			ok: true,
			// The final-confirm summary follows — give the model the spoken total.
			total_text: total ? fmtFt(total) : undefined,
			total_say: total ? priceToHu(total) : undefined,
		}
	}

	private toolSuggestReplies(args: Record<string, unknown>): unknown {
		const labels = Array.isArray(args.labels) ? args.labels.map((l) => String(l)).slice(0, 3) : []
		if (labels.length === 0) return { ok: false, error: 'no_labels' }
		const primary = typeof args.primary_index === 'number' ? args.primary_index : -1
		store.set({
			chips: labels.map((label, i) => ({ id: `r${i}`, label, primary: i === primary })),
			statusHint: 'none',
		})
		return { ok: true, displayed: true }
	}

	/* ---------------- filing facts + the final confirm ---------------- */

	private fileFact(
		key: RowKey,
		label: string,
		value: string,
		opts: { note?: string; noteGreen?: boolean; green?: boolean; holdMs?: number } = {},
	): void {
		store.set({ sum: { ...store.get().sum, [key]: value } })
		store.set({ card: { label, value, note: opts.note, noteGreen: opts.noteGreen, green: opts.green } })
		const holdMs = opts.holdMs ?? CARD_MS
		this.st(() => {
			const card = store.get().card
			if (!card || card.value !== value) return
			store.set({ card: { ...card, leaving: true } })
			this.st(() => {
				const cur = store.get().card
				if (cur && cur.value === value) store.set({ card: null })
			}, CARD_OUT_MS)
		}, holdMs)
	}

	/** All six points filed → the UI (not the model) shows the price breakdown
	    and the „Megrendelem" primary — §4.1: /orderInstall is not a tool. */
	private maybeFinalConfirm(): void {
		const o = this.order
		if (!o.vehicleId || !o.battery || !o.address || !o.slot || !o.contact || !o.payment) return
		if (this.finalConfirmShown || store.get().done) return
		this.finalConfirmShown = true
		notifyCheckoutStarted()

		const battery = o.battery
		const installFee = battery.price_total - battery.price
		this.st(() => {
			store.set({
				price: {
					total: fmtFt(battery.price_total),
					lines: [
						{ k: `${battery.name} akkumulátor`, v: fmtFt(battery.price) },
						{ k: 'Kiszállás és beszerelés', v: fmtFt(installFee) },
						{ k: 'Régi akku elszállítása', v: fmtFt(0) },
					],
				},
				chips: [
					{ id: 'order', label: `Megrendelem · ${fmtFt(battery.price_total)}`, primary: true },
					{ id: 'modify', label: 'Még módosítanék' },
				],
			})
		}, CARD_MS + CARD_OUT_MS + 100)
	}

	private async submitOrder(): Promise<void> {
		const o = this.order
		if (this.submitting) return

		// NEVER silent: a tap on „Megrendelem" with a missing piece must tell
		// the customer what is missing (via the assistant), not do nothing —
		// "the button did nothing" is indistinguishable from a bug.
		const missing: string[] = []
		if (!o.vehicleId) missing.push('az autó')
		if (!o.battery) missing.push('az akkumulátor')
		if (!o.address) missing.push('a kiszállási cím')
		if (!o.slot) missing.push('az időpont')
		if (!o.contact) missing.push('a név/telefonszám/e-mail')
		if (!o.payment) missing.push('a fizetési mód')
		if (missing.length > 0) {
			logDiag('submit_missing', missing.join(','))
			this.finalConfirmShown = false
			store.set({ chips: [], price: null })
			this.createItem(
				'user',
				`[RENDSZER] A vevő a Megrendelem gombra koppintott, de még hiányzik: ${missing.join(', ')}. Kérje el a hiányzó adatot a megfelelő eszközzel, majd a rendszer újra felkínálja a megerősítést.`,
			)
			return
		}
		if (!o.battery || !o.address || !o.slot || !o.contact || !o.payment) return
		this.submitting = true
		store.set({ chips: [], orb: 'thinking' })
		store.setEcho('Megrendelem.')

		try {
			const res = await apiClient.post('/order', {
				name: o.contact.name,
				phone: o.contact.phone,
				email: o.contact.email,
				product_id: String(o.productId),
				vehicle_id: String(o.vehicleId),
				zip: o.address.pc,
				street: o.address.street,
				house_number: o.address.hn,
				order_day: o.slot.date,
				order_time: o.slot.time,
				// On-site payment either way; the order schema only knows
				// keszpenz/simplepay-card, so the card preference travels in the
				// comment for the technician (terminal needed).
				fizetesi_mod: 'keszpenz',
				comment:
					o.payment === 'card'
						? 'Hangos rendelés (Akku Most) — a helyszínen KÁRTYÁVAL fizetne.'
						: 'Hangos rendelés (Akku Most) — készpénzes fizetés a helyszínen.',
			})

			const data = res.data as { order_number?: string; result?: string; message?: string }
			if (!data.order_number) throw new Error(data.message || 'missing order_number')

			logDiag('order_ok', data.order_number)
			store.stopHold()
			const price = store.get().price
			store.set({
				done: true,
				holdOn: false,
				chips: [],
				statusHint: 'none',
				price: price ? { ...price, leaving: true } : null,
			})
			this.st(() => {
				store.set({
					price: null,
					success: { orderNo: data.order_number!, slot: o.slot!.label },
					assistantLine: '',
					userEcho: '',
				})
			}, 320)
			this.createItem(
				'system',
				`[RENDSZER] A rendelés sikeresen rögzítve, rendelésszám: ${data.order_number}. Köszönje meg röviden, és mondja el, hogy kérdés esetén a Hívás gombbal hívhatnak minket.`,
			)
		} catch (err: unknown) {
			this.submitting = false
			this.finalConfirmShown = false
			const data = (err as { response?: { data?: { message?: string } } }).response?.data
			const msg = typeof data?.message === 'string' ? data.message : 'ismeretlen hiba'
			logDiag('order_fail', msg)
			this.createItem(
				'system',
				`[RENDSZER] A rendelés leadása nem sikerült (${msg}). Kérjen elnézést, és ajánlja fel a Hívás gombot — vagy próbálják újra a Megrendelem gombbal.`,
			)
			store.set({
				chips: [
					{ id: 'order', label: `Megrendelem · ${fmtFt(o.battery.price_total)}`, primary: true },
					{ id: 'modify', label: 'Még módosítanék' },
				],
			})
		}
	}

	/* ---------------- draft handoff ---------------- */

	/** Form-data-shaped payload — the exact keys the ügyfélszolgálat page's
	    session-restore path consumes (FillFormBySession). */
	private draftPayload(): Record<string, string> {
		const o = this.order
		const payload: Record<string, string> = {}
		if (o.vehicleRow) {
			payload.fuel = o.vehicleRow.fuel_type || ''
			payload.brand = o.vehicleRow.brand || ''
			payload.type = o.vehicleRow.model_type || o.vehicleRow.model || ''
			payload.carid = String(o.vehicleId)
		}
		if (o.battery) {
			payload.order_battery_id = String(o.battery.id)
			payload.battery_name = o.battery.name
			payload.priceSumBattery = String(o.battery.price)
			payload.priceSumFee = String(o.battery.price_total - o.battery.price)
		}
		if (o.address) {
			payload.pc = o.address.pc
			payload.city = o.address.city
			payload.st = o.address.street
			payload.hn = o.address.hn
		}
		if (o.slot) {
			payload.orderDay = o.slot.date
			payload.orderTime = o.slot.time
		}
		if (o.contact) {
			payload.name = o.contact.name
			payload.ph = o.contact.phone
			if (o.contact.email) payload.em = o.contact.email
		}
		if (o.payment) payload.payment_method = o.payment === 'card' ? 'kartya' : 'keszpenz'
		return payload
	}

	/** Keep the draft current once minted (§4.3) — no rows for silent visitors. */
	private async upsertDraft(): Promise<void> {
		if (!this.draftEnabled || !this.draftMinted) return
		try {
			await apiClient.post('/voice/draft', { payload: this.draftPayload(), mint_code: false })
		} catch {
			/* draft sync is best-effort */
		}
	}
}
